import { useState } from "react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";

/**
 * ChangePasswordCard — self-service password change for any signed-in
 * user. Doubles as the admin's post-deploy nudge to rotate the seed
 * password on production.
 */
export default function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New passwords don't match");
    if (next === current) return toast.error("New password must be different from the current one");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(data.message || "Password updated");
      reset();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="me-change-password"
      className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6"
    >
      <div className="uppercase-label inline-flex items-center gap-1">
        <KeyRound size={14} /> Change password
      </div>
      <p className="text-sm text-[color:var(--mano-ink-soft)] mt-2 max-w-lg">
        Rotate your password any time. We'll ask for your current one first —
        a small guard so a stolen session cookie can't silently take over.
      </p>
      <form className="mt-4 grid gap-3 max-w-md" onSubmit={submit}>
        <PwField
          label="Current password"
          testid="pw-current"
          value={current}
          onChange={setCurrent}
          show={showCurrent}
          toggle={() => setShowCurrent((v) => !v)}
          autoComplete="current-password"
        />
        <PwField
          label="New password"
          testid="pw-new"
          value={next}
          onChange={setNext}
          show={showNext}
          toggle={() => setShowNext((v) => !v)}
          hint="At least 8 characters. Use a fresh phrase you haven't used elsewhere."
          autoComplete="new-password"
        />
        <PwField
          label="Confirm new password"
          testid="pw-confirm"
          value={confirm}
          onChange={setConfirm}
          show={showNext}
          toggle={() => setShowNext((v) => !v)}
          autoComplete="new-password"
        />
        <div>
          <button
            data-testid="pw-submit"
            type="submit"
            disabled={busy || !current || !next || !confirm}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold text-sm disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {busy ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </section>
  );
}

function PwField({ label, testid, value, onChange, show, toggle, hint, autoComplete }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-[color:var(--mano-ink)] mb-1">{label}</div>
      <div className="relative">
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          value={value}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-11 rounded-xl border border-[color:var(--mano-border)] bg-white px-3 pr-11 text-sm focus:outline-none focus:border-[color:var(--mano-primary)]"
        />
        <button
          type="button"
          onClick={toggle}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full flex items-center justify-center text-[color:var(--mano-ink-soft)] hover:bg-[color:var(--mano-border)]/40"
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
      {hint && <div className="text-xs text-[color:var(--mano-ink-soft)] mt-1">{hint}</div>}
    </label>
  );
}
